import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $createListNode } from "@lexical/list";
import { $createCodeNode } from "@lexical/code";
import { 
  $getSelection, 
  $isRangeSelection, 
  $createParagraphNode, 
  TextNode,
  $insertNodes,
  $createTextNode,
} from "lexical";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { useCallback, useMemo, useState } from "react";
import { $setBlocksType } from "@lexical/selection";
import {
  Type,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Code,
  Minus,
  LucideIcon,
} from "lucide-react";

class SlashCommandOption extends MenuOption {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  iconColor?: string;
  command: string;
  shortcut?: string;
  group: string;

  constructor(
    id: string,
    label: string,
    description: string,
    icon: LucideIcon,
    command: string,
    group: string,
    options?: { iconColor?: string; shortcut?: string }
  ) {
    super(id);
    this.id = id;
    this.label = label;
    this.description = description;
    this.icon = icon;
    this.command = command;
    this.group = group;
    this.iconColor = options?.iconColor;
    this.shortcut = options?.shortcut;
  }
}

interface CommandGroup {
  name: string;
  items: SlashCommandOption[];
}

function createCommandGroups(): CommandGroup[] {
  return [
    {
      name: "Basic blocks",
      items: [
        new SlashCommandOption("text", "Text", "Plain text", Type, "paragraph", "Basic blocks"),
        new SlashCommandOption("h1", "Heading 1", "Large section heading", Heading1, "h1", "Basic blocks", { shortcut: "#" }),
        new SlashCommandOption("h2", "Heading 2", "Medium section heading", Heading2, "h2", "Basic blocks", { shortcut: "##" }),
        new SlashCommandOption("h3", "Heading 3", "Small section heading", Heading3, "h3", "Basic blocks", { shortcut: "###" }),
        new SlashCommandOption("bullet", "Bulleted list", "Create a bulleted list", List, "bullet", "Basic blocks", { shortcut: "-" }),
        new SlashCommandOption("number", "Numbered list", "Create a numbered list", ListOrdered, "number", "Basic blocks", { shortcut: "1." }),
        new SlashCommandOption("todo", "To-do list", "Track tasks with checkboxes", CheckSquare, "todo", "Basic blocks", { shortcut: "[]" }),
        new SlashCommandOption("quote", "Quote", "Capture a quote", Quote, "quote", "Basic blocks", { shortcut: '"' }),
        new SlashCommandOption("divider", "Divider", "Visual divider", Minus, "divider", "Basic blocks", { shortcut: "---" }),
        new SlashCommandOption("code-block", "Code block", "Capture code snippet", Code, "code-block", "Basic blocks", { shortcut: "```" }),
      ],
    },
  ];
}

function SlashMenuItem({
  item,
  isSelected,
  onSelect,
}: {
  item: SlashCommandOption;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const Icon = item.icon;

  return (
    <div
      className={`relative flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm outline-none transition-colors ${
        isSelected ? "bg-accent" : "hover:bg-accent/50"
      }`}
      onClick={onSelect}
    >
      <div className="flex h-5 w-5 items-center justify-center">
        <Icon
          className="h-4 w-4 flex-shrink-0"
          style={item.iconColor ? { color: item.iconColor } : undefined}
        />
      </div>
      <span className="flex-1 truncate font-medium">{item.label}</span>
      {item.shortcut && (
        <span className="text-xs text-muted-foreground">{item.shortcut}</span>
      )}
    </div>
  );
}

export function SlashCommandPlugin() {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState("");

  const triggerFn = useBasicTypeaheadTriggerMatch("/", { minLength: 0 });

  const commandGroups = useMemo(() => createCommandGroups(), []);

  const filteredGroups = useMemo(() => {
    if (!query) return commandGroups;

    return commandGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.label.toLowerCase().includes(query.toLowerCase()) ||
            item.description.toLowerCase().includes(query.toLowerCase())
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [query, commandGroups]);

  const flatOptions = useMemo(() => {
    return filteredGroups.flatMap((group) => group.items);
  }, [filteredGroups]);

  const onSelectOption = useCallback(
    (
      selectedOption: SlashCommandOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        if (nodeToReplace) {
          nodeToReplace.remove();
        }

        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          switch (selectedOption.command) {
            case "h1":
              $setBlocksType(selection, () => $createHeadingNode("h1"));
              break;
            case "h2":
              $setBlocksType(selection, () => $createHeadingNode("h2"));
              break;
            case "h3":
              $setBlocksType(selection, () => $createHeadingNode("h3"));
              break;
            case "bullet":
              $setBlocksType(selection, () => $createListNode("bullet"));
              break;
            case "number":
              $setBlocksType(selection, () => $createListNode("number"));
              break;
            case "paragraph":
              $setBlocksType(selection, () => $createParagraphNode());
              break;
            case "todo":
              $setBlocksType(selection, () => $createListNode("check"));
              break;
            case "quote":
              $setBlocksType(selection, () => $createQuoteNode());
              break;
            case "divider":
              editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
              break;
            case "code-block":
              $setBlocksType(selection, () => $createCodeNode());
              break;
          }
        }
      });
      closeMenu();
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<SlashCommandOption>
      triggerFn={triggerFn}
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      options={flatOptions}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp }
      ) =>
        flatOptions.length > 0 ? (
          <div
            className="fixed z-[9999] w-72 rounded-xl border border-border bg-background shadow-2xl"
            style={{
              top: anchorElementRef.current
                ? anchorElementRef.current.getBoundingClientRect().bottom + 4
                : 0,
              left: anchorElementRef.current
                ? anchorElementRef.current.getBoundingClientRect().left
                : 0,
            }}
          >
            <div className="max-h-[min(400px,40vh)] overflow-y-auto overflow-x-hidden p-1">
              {filteredGroups.map((group, groupIndex) => (
                <div key={group.name}>
                  {groupIndex > 0 && <div className="mx-2 my-1 h-px bg-border" />}
                  <div className="px-2 py-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {group.name}
                    </span>
                  </div>
                  {group.items.map((item) => {
                    const flatIndex = flatOptions.findIndex(
                      (opt) => opt.id === item.id
                    );
                    return (
                      <SlashMenuItem
                        key={item.id}
                        item={item}
                        isSelected={flatIndex === selectedIndex}
                        onSelect={() => selectOptionAndCleanUp(item)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="border-t border-border px-2 py-1.5">
              <span className="text-xs text-muted-foreground">
                Type{" "}
                <kbd className="rounded bg-muted px-1 py-0.5 text-[10px]">/</kbd>{" "}
                on the page
              </span>
            </div>
          </div>
        ) : query ? (
          <div
            className="fixed z-[9999] w-72 rounded-xl border border-border bg-background p-4 shadow-2xl"
            style={{
              top: anchorElementRef.current
                ? anchorElementRef.current.getBoundingClientRect().bottom + 4
                : 0,
              left: anchorElementRef.current
                ? anchorElementRef.current.getBoundingClientRect().left
                : 0,
            }}
          >
            <p className="text-center text-sm text-muted-foreground">
              No results found
            </p>
          </div>
        ) : null
      }
    />
  );
}
